/**
 * Pre-capture stabilization barrier.
 *
 * `document.fonts.ready` alone is not sufficient — it resolves when font
 * metadata has loaded, not when every glyph has been rasterized into the
 * GPU cache, and it doesn't cover images at all. Capturing before any
 * resource is fully decoded means html2canvas may rasterize fallback
 * glyphs / partially-decoded images. This module waits for everything.
 *
 * Sequence:
 *   1. FontFaceSet.ready                 — all CSS @font-face metadata loaded
 *   2. Promise.all([img.decode()])       — every <img> fully decoded to GPU
 *   3. requestAnimationFrame x2          — next paint has committed
 *   4. setTimeout(0) microtask flush     — any deferred layout/style work
 *
 * After this barrier, getBoundingClientRect / getComputedStyle return
 * the exact values that will be rasterized.
 *
 * Also exports two helpers for replay mode (deterministic repeated
 * exports): installReplayMode() + a state-hash for cache invalidation.
 */

export async function awaitExportStability(canvasEl) {
  // (1) Font metadata loaded.
  if (typeof document !== 'undefined' && document.fonts?.ready) {
    try { await document.fonts.ready; } catch { /* fall through */ }
  }

  // (2) Image decode — every <img> in the canvas must be fully decoded
  //     before we read computed bboxes for the text wrap / clip math.
  if (canvasEl && canvasEl.querySelectorAll) {
    const imgs = Array.from(canvasEl.querySelectorAll('img'));
    await Promise.all(imgs.map((img) => {
      if (img.complete && img.naturalWidth > 0) {
        return img.decode ? img.decode().catch(() => undefined) : Promise.resolve();
      }
      return new Promise((resolve) => {
        const done = () => {
          img.removeEventListener('load', done);
          img.removeEventListener('error', done);
          if (img.decode) img.decode().catch(() => undefined).then(resolve);
          else resolve();
        };
        img.addEventListener('load',  done, { once: true });
        img.addEventListener('error', done, { once: true });
      });
    }));
  }

  // (3) Two animation frames — first commits any pending style/layout work,
  //     second guarantees the paint has flushed.
  await new Promise((r) => requestAnimationFrame(() => r()));
  await new Promise((r) => requestAnimationFrame(() => r()));

  // (4) Microtask flush.
  await new Promise((r) => setTimeout(r, 0));
}

/**
 * Compute a hash of every environment dimension whose change should
 * invalidate any previously-captured export state (font set, zoom, DPR,
 * viewport, canvas size). Two snapshots with different hashes describe
 * incompatible rendering conditions — measurements taken before the
 * change must NOT be reused for a capture taken after.
 */
export function computeStateHash(canvasEl) {
  if (typeof window === 'undefined' || !canvasEl) return '';
  const cs = window.getComputedStyle(canvasEl);
  const fontFamilies = new Set();
  // Collect every font-family inside the canvas subtree — a load that
  // adds a new face changes the hash.
  if (canvasEl.querySelectorAll) {
    canvasEl.querySelectorAll('[style*="font-family"]').forEach((el) => {
      const ff = el.style.fontFamily;
      if (ff) fontFamilies.add(ff);
    });
  }
  const parts = [
    `dpr=${window.devicePixelRatio || 1}`,
    `vp=${window.innerWidth}x${window.innerHeight}`,
    `cs=${canvasEl.offsetWidth}x${canvasEl.offsetHeight}`,
    `xf=${cs.transform || 'none'}`,
    `fz=${cs.fontSize}`,
    `ff=${Array.from(fontFamilies).sort().join('|')}`,
    `fc=${document?.fonts?.size ?? 0}`,
  ];
  return parts.join(';');
}

/**
 * Replay mode: install a global stylesheet that freezes every source of
 * frame-to-frame non-determinism (animations, transitions, blinking
 * cursors, selection overlays, hover effects). Returns a teardown
 * function — always call it in a `finally` so the editor returns to
 * interactive state.
 *
 * Mutates only the document head (one <style> node); the canvas DOM
 * itself is not touched.
 */
export function installReplayMode() {
  if (typeof document === 'undefined') return () => {};
  const STYLE_ID = '__export-replay-stylesheet';
  if (document.getElementById(STYLE_ID)) return () => {};

  const style = document.createElement('style');
  style.id = STYLE_ID;
  style.textContent = `
    /* Freeze every running animation/transition: replay mode is for
       deterministic repeated exports. */
    *, *::before, *::after {
      animation-play-state: paused !important;
      animation-duration: 0s !important;
      animation-delay: 0s !important;
      transition-duration: 0s !important;
      transition-delay: 0s !important;
      caret-color: transparent !important;
    }
    /* Hide editor chrome that would otherwise bleed into captures of
       sub-trees rendered through the same DOM. */
    [data-export-hide],
    .canvas-cursor-blink,
    ::selection,
    ::-moz-selection {
      display: none !important;
      background: transparent !important;
    }
    /* Disable hover-only adornments. */
    *:hover {
      filter: none !important;
    }
  `;
  document.head.appendChild(style);
  return () => { if (style.parentNode) style.parentNode.removeChild(style); };
}
